import { useEffect, useMemo } from 'react';
import { useTokenValidation } from './useTokenValidation';
import { useProfile } from '@/contexts/UserProfileContext';
import { useUserRoles, AppRole } from './useUserRoles';
import { authLogger } from '@/utils/authLogger';

export interface AdminAccessResult {
  // Loading states
  isLoading: boolean;
  isReady: boolean;
  
  // Authentication
  isAuthenticated: boolean;
  
  // Admin status (only reliable when isReady === true)
  isAdmin: boolean;
  isSuperAdmin: boolean;
  hasElevatedAccess: boolean;
  
  // User info
  userEmail: string | null;
  profileId: string | null;
  activeRole: AppRole | null;
  allRoles: AppRole[];
  
  // Stale-while-revalidate indicators
  isStale: boolean;
  isRefreshing: boolean;
  
  // Actions
  refetch: () => Promise<void>;
  setActiveRole: (role: AppRole) => void;
}

/**
 * Centralized hook for admin access that combines:
 * - Token validation (fast, cached)
 * - Profile loading (user data)
 * - Role checking (database source of truth)
 * 
 * Use `isReady` to ensure all data is loaded before trusting admin status.
 */
export const useAdminAccess = (): AdminAccessResult => {
  // Step 1: Token validation (fastest, from cache)
  const { 
    result: tokenResult, 
    isLoading: tokenLoading,
    isStale: tokenIsStale,
    isRefreshing: tokenRefreshing
  } = useTokenValidation();
  
  // Step 2: Profile loading (depends on token being valid)
  const { profile, isLoading: profileLoading } = useProfile();
  
  // Step 3: Role fetching (depends on profile.id)
  const { 
    isAdmin: roleIsAdmin, 
    isSuperAdmin,
    hasElevatedAccess,
    activeRole,
    roles,
    isLoading: rolesLoading,
    refetch: refetchRoles,
    setActiveRole
  } = useUserRoles(profile?.id);
  
  // Compute composite loading state
  const isTokenValid = tokenResult?.isValid ?? false;
  const hasProfileId = !!profile?.id;
  
  // Loading if: token loading OR (token valid AND (profile loading OR (has profile AND roles loading)))
  const isLoading = tokenLoading || (isTokenValid && (profileLoading || (hasProfileId && rolesLoading)));
  
  // Ready when: not loading AND we have profile AND roles have loaded
  const isReady = !isLoading && hasProfileId && !rolesLoading;
  
  // Admin status - memoized for performance, ONLY trust when chain is complete
  const isAdmin = useMemo(() => 
    isReady ? (roleIsAdmin || tokenResult?.isAdmin === true) : false,
    [isReady, roleIsAdmin, tokenResult?.isAdmin]
  );
  
  const finalIsSuperAdmin = useMemo(() => 
    isReady ? isSuperAdmin : false,
    [isReady, isSuperAdmin]
  );
  
  const finalHasElevatedAccess = useMemo(() => 
    isReady ? hasElevatedAccess : false,
    [isReady, hasElevatedAccess]
  );
  
  // Debug logging for admin access decisions
  useEffect(() => {
    if (isReady && isTokenValid) {
      authLogger.adminAccessCheck({
        isReady,
        isAdmin,
        profileId: profile?.id || null,
        roles,
        source: roleIsAdmin ? 'database' : (tokenResult?.isAdmin ? 'token' : 'none')
      });
      
      if (isAdmin) {
        authLogger.adminAccessGranted(tokenResult?.userEmail || 'unknown', roles);
      }
    }
  }, [isReady, isAdmin, isTokenValid, profile?.id, roles, roleIsAdmin, tokenResult?.isAdmin, tokenResult?.userEmail]);
  
  return {
    isLoading,
    isReady,
    isAuthenticated: isTokenValid,
    isAdmin,
    isSuperAdmin: finalIsSuperAdmin,
    hasElevatedAccess: finalHasElevatedAccess,
    userEmail: tokenResult?.userEmail ?? null,
    profileId: profile?.id ?? null,
    activeRole,
    allRoles: roles,
    isStale: tokenIsStale ?? false,
    isRefreshing: tokenRefreshing ?? false,
    refetch: refetchRoles,
    setActiveRole
  };
};

export default useAdminAccess;
